import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  type RecurrenceBulkEditPreview,
  type RecurrenceRule,
  type TemplateEdit,
  useApplyBulkRecurrenceEdit,
  usePreviewBulkRecurrenceEdit,
} from '@/entities/recurrence';
import { isApiError, queryKeys } from '@/shared/api';
import { FormError } from '@/shared/forms';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { Button, Dialog, LoadingState, OverdueToggle, TextField, TimeField } from '@/shared/ui';

import styles from './Recurrence.module.css';

interface Props {
  sourceCardId: string;
  rule: RecurrenceRule;
  onClose: () => void;
  onDone: () => void;
}

const clock = (value: string | null): string => value?.slice(0, 5) ?? '';

const validClock = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

export function BulkRecurrenceEditDialog({ sourceCardId, rule, onClose, onDone }: Props) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();

  const preview = usePreviewBulkRecurrenceEdit(sourceCardId);
  const apply = useApplyBulkRecurrenceEdit(sourceCardId);

  const [form, setForm] = useState({
    title: rule.title ?? '',
    startTime: clock(rule.start_time),
    endTime: clock(rule.end_time),
    length: String(rule.length_days ?? 0),
    overdue: rule.overdue_enabled === true,
  });

  const [previewSignature, setPreviewSignature] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const length = Number(form.length);

  const changes: TemplateEdit = {
    ...(form.title.trim() !== (rule.title ?? '') && {
      title: form.title.trim(),
    }),

    ...(rule.is_event &&
      form.startTime !== clock(rule.start_time) && {
        start_time: form.startTime,
      }),

    ...(rule.has_deadline &&
      form.endTime !== clock(rule.end_time) && {
        end_time: form.endTime,
      }),

    ...(rule.is_event &&
      length !== rule.length_days && {
        length_days: length,
      }),

    ...(rule.is_event &&
      form.overdue !== (rule.overdue_enabled === true) && {
        overdue_enabled: form.overdue,
      }),
  };

  const signature = JSON.stringify({
    version: rule.version,
    changes,
  });

  const hasChanges = Object.keys(changes).length > 0;

  const valid =
    form.title.trim().length > 0 &&
    form.title.trim().length <= 500 &&
    (!rule.is_event || form.startTime === clock(rule.start_time) || validClock(form.startTime)) &&
    (!rule.has_deadline || form.endTime === clock(rule.end_time) || validClock(form.endTime)) &&
    (!rule.is_event || (Number.isInteger(length) && length >= 0 && length <= 3650)) &&
    (!rule.is_event ||
      length > 0 ||
      !form.startTime ||
      !form.endTime ||
      form.endTime >= form.startTime);

  const currentPreview: RecurrenceBulkEditPreview | null =
    !stale && preview.isSuccess && previewSignature === signature ? preview.data : null;

  const changedAfterPreview =
    preview.isSuccess && previewSignature !== null && previewSignature !== signature;

  const invalidateRule = () => {
    void client.invalidateQueries({
      queryKey: queryKeys.cards.recurrence(sourceCardId),
    });
  };

  const requestPreview = () => {
    if (!valid || !hasChanges || preview.isPending) return;

    setStale(false);
    setPreviewSignature(signature);

    preview.mutate(
      {
        scope: 'series',
        expected_version: rule.version,
        template: changes,
      },
      {
        onError: (error) => {
          if (isApiError(error) && error.status === 409) {
            setStale(true);
            setPreviewSignature(null);
            invalidateRule();
          }
        },
      },
    );
  };

  const save = () => {
    if (!currentPreview || apply.isPending) return;

    apply.mutate(
      {
        scope: 'series',
        expected_version: currentPreview.rule_version,
        expected_from_date: currentPreview.from_date,
        expected_materialized_cards: currentPreview.materialized_cards,
        expected_fully_editable: currentPreview.fully_editable,
        expected_partially_editable: currentPreview.partially_editable,
        expected_protected: currentPreview.protected,
        expected_title_updates: currentPreview.title_updates,
        expected_deadline_updates: currentPreview.deadline_updates,
        template: changes,
      },
      {
        onSuccess: onDone,
        onError: (error) => {
          if (isApiError(error) && error.status === 409) {
            // The previous confirmation is invalid.
            // Never retry a write automatically.
            setStale(true);
            setPreviewSignature(null);
            preview.reset();
            invalidateRule();
          }
        },
      },
    );
  };

  const close = () => {
    if (!apply.isPending) onClose();
  };

  return (
    <Dialog title={t('recurrence.bulkEdit.title')} onClose={close} size="lg">
      <div className={styles.policy} data-testid="recurrence-bulk-edit">
        <p className={styles.note}>{t('recurrence.bulkEdit.description')}</p>

        <TextField
          label={t('recurrence.template.title')}
          value={form.title}
          maxLength={500}
          onChange={(event) => {
            setForm({ ...form, title: event.target.value });
          }}
        />

        {rule.is_event && (
          <TimeField
            label={t('recurrence.template.startTime')}
            value={form.startTime}
            onChange={(value) => {
              setForm({ ...form, startTime: value });
            }}
          />
        )}

        {rule.has_deadline && (
          <TimeField
            label={t('recurrence.template.endTime')}
            value={form.endTime}
            onChange={(value) => {
              setForm({ ...form, endTime: value });
            }}
          />
        )}

        {rule.is_event && (
          <>
            <TextField
              label={t('recurrence.template.length')}
              type="number"
              min={0}
              max={3650}
              value={form.length}
              onChange={(event) => {
                setForm({ ...form, length: event.target.value });
              }}
            />

            <OverdueToggle
              value={form.overdue}
              onChange={(overdue) => {
                setForm({ ...form, overdue });
              }}
            />
          </>
        )}

        <p className={styles.note}>{t('recurrence.bulkEdit.timeNote')}</p>

        {changedAfterPreview && (
          <p className={styles.warn}>{t('recurrence.bulkEdit.recalculate')}</p>
        )}

        {stale && (
          <p className={styles.warn} role="alert">
            {t('recurrence.bulkEdit.stale')}
          </p>
        )}

        <Button
          variant="ghost"
          disabled={!valid || !hasChanges || preview.isPending || apply.isPending}
          onClick={requestPreview}
        >
          {t('recurrence.bulkEdit.preview')}
        </Button>

        {preview.isPending && <LoadingState />}

        {currentPreview && (
          <div data-testid="recurrence-bulk-edit-preview">
            <p>
              {t('recurrence.bulkEdit.from', {
                date: formatCalendarDate(currentPreview.from_date, i18n.language),
              })}
            </p>

            <p>
              {t('recurrence.bulkEdit.total', {
                count: currentPreview.materialized_cards,
              })}
            </p>

            <p>
              {t('recurrence.bulkEdit.full', {
                count: currentPreview.fully_editable,
              })}
            </p>

            <p>
              {t('recurrence.bulkEdit.partial', {
                count: currentPreview.partially_editable,
              })}
            </p>

            <p>
              {t('recurrence.bulkEdit.protected', {
                count: currentPreview.protected,
              })}
            </p>

            <p>
              {t('recurrence.bulkEdit.titleUpdates', {
                count: currentPreview.title_updates,
              })}
            </p>

            <p>
              {t('recurrence.bulkEdit.deadlineUpdates', {
                count: currentPreview.deadline_updates,
              })}
            </p>

            <p className={styles.warn}>{t('recurrence.bulkEdit.notice')}</p>
          </div>
        )}

        <FormError error={preview.error ?? apply.error} />

        <div className={styles.actions}>
          <Button variant="ghost" disabled={apply.isPending} onClick={close}>
            {t('common.cancel')}
          </Button>

          <Button
            variant="primary"
            disabled={!currentPreview || !valid || apply.isPending || preview.isPending}
            onClick={save}
          >
            {t('recurrence.bulkEdit.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
